/**
 * Service Icon Mapping Utility
 * @description 서비스 아이콘 매핑 — lucide-react 전체 `icons` + 프로젝트 별칭(한글·레거시 이름)
 * backend/data/services.json의 icon 필드와 동기화됨
 */

import { Shield } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import {
  GitBranch,
  FolderOpen,
  Archive,
  LayoutDashboard,
  AlertTriangle,
  FileText,
  SlidersHorizontal,
  ClipboardList,
  Bug,
  CheckCircle2,
  ShieldCheck,
  LineChart,
  Bot,
  ScrollText,
  KeyRound,
  Briefcase,
  TrendingUp,
  BrainCircuit,
  MessageSquare,
  PieChart,
  ShieldAlert,
  LockKeyhole,
  Network,
  HardDrive,
  Cloud,
  LayoutGrid,
  Settings2,
  Save,
  ShieldPlus,
  Cuboid,
  /** 평가·판단·생각 등 의미 별칭용 */
  Brain,
  BrainCog,
  Lightbulb,
  Scale,
  Gavel,
  ClipboardCheck,
  BadgeCheck,
  FileSearch,
  ScanSearch,
  ListChecks,
  BookOpenCheck,
  Medal,
  CircleQuestionMark,
  MessageCircleQuestionMark,
  Eye,
  Sparkles,
  Target,
  Waypoints,
} from 'lucide-react'
import { lucideFullIconMap } from '@/utils/lucideFullIconMap'

/**
 * Lucide 기본 이름과 다른 문자열로 저장된 서비스용 별칭 (한국어·레거시)
 * lucide 전체 맵보다 나중에 spread 되어 동일 키가 있으면 여기가 우선합니다.
 */
const serviceIconAliases: Record<string, LucideIcon> = {
  Topology: GitBranch,
  FileExplorer: FolderOpen,
  Asset: Archive,
  Dashboard: LayoutDashboard,
  Alert: AlertTriangle,
  Report: FileText,
  Control: SlidersHorizontal,
  ServiceRequest: ClipboardList,
  Vulnerability: Bug,
  Evaluation: CheckCircle2,
  Risk: AlertTriangle,
  Mitigation: ShieldCheck,
  Graph: LineChart,
  Automation: Bot,

  정책: ScrollText,
  자산: Archive,
  보호: ShieldCheck,
  접근제어: KeyRound,
  제어: SlidersHorizontal,
  서비스요청: ClipboardList,
  서비스: Briefcase,
  분석: TrendingUp,
  AI: BrainCircuit,
  Chatbot: MessageSquare,
  자동화: Bot,
  통계: PieChart,
  방화벽: ShieldAlert,
  VPN: LockKeyhole,
  네트워크: Network,
  장비: HardDrive,
  클라우드: Cloud,

  시각화: LayoutGrid,
  세부설정: Settings2,
  저장: Save,
  보안쉴드: ShieldPlus,
  육면체: Cuboid,

  /** 평가·판단·생각·검토 관련 별칭 (services.json·아이콘 검색용) */
  평가: ClipboardCheck,
  판단: Gavel,
  저울: Scale,
  생각: Brain,
  사고: BrainCog,
  검토: FileSearch,
  심사: BadgeCheck,
  추론: BrainCircuit,
  통찰: Lightbulb,
  독서: BookOpenCheck,
  의문: CircleQuestionMark,
  질문: MessageCircleQuestionMark,
  관찰: Eye,
  아이디어: Sparkles,
  목표: Target,
  경로판단: Waypoints,
  체크리스트: ListChecks,
  점수: Medal,
  스캔검토: ScanSearch,

  Judgment: Gavel,
  Thinking: Brain,
  Insight: Lightbulb,
  Reasoning: BrainCircuit,
  Deliberation: Scale,
  Review: FileSearch,
  Scrutiny: ScanSearch,
}

/** lucide 패키지 전체 아이콘 + 프로젝트 별칭 */
export const iconMap: Record<string, LucideIcon> = {
  ...lucideFullIconMap,
  ...serviceIconAliases,
}

/**
 * 아이콘 이름으로 아이콘 컴포넌트를 가져옴
 * @param iconName 아이콘 이름 (backend/data/services.json의 icon 필드 값)
 * @param fallback 기본 아이콘 (없을 경우 사용)
 */
export function getIconComponent(iconName: string | undefined, fallback: LucideIcon = Shield): LucideIcon {
  if (!iconName) return fallback
  return iconMap[iconName] ?? fallback
}

/** 아이콘 선택 UI용 — 정렬된 전체 이름 목록 */
export const availableIconNames: string[] = Object.keys(iconMap).sort((a, b) => a.localeCompare(b, 'en'))

/** services.json·별칭 기반 빠른 선택 목록 (전체 lucide 렌더링 방지) */
const commonServiceLucideIcons = [
  'Shield',
  'Package',
  'Server',
  'Users',
  'Settings',
  'Cloud',
  'ScrollText',
  'ClipboardList',
  'UserCog',
  'Tags',
  'TvMinimal',
  'Nfc',
  'LayoutGrid',
  'Bot',
  'BrainCircuit',
] as const

export const curatedIconNames: string[] = Array.from(
  new Set([...commonServiceLucideIcons, ...Object.keys(serviceIconAliases)])
).sort((a, b) => a.localeCompare(b, 'en'))
